import { Service, signal } from '@angular/core';

export type ToastVariant = 'success' | 'error' | 'neutral';

export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly variant: ToastVariant;
  readonly action: string;
}

export interface ToastOptions {
  readonly duration?: number;
  readonly action?: string;
}

const DEFAULT_DURATION = 5000;

@Service()
export class ToastService {
  private nextId = 0;

  private readonly queue = signal<readonly Toast[]>([]);

  readonly toasts = this.queue.asReadonly();

  show(message: string, variant: ToastVariant = 'neutral', options: ToastOptions = {}): number {
    const id = this.nextId++;
    const toast: Toast = { id, message, variant, action: options.action ?? 'Dismiss' };

    this.queue.update((toasts) => [...toasts, toast]);

    const duration = options.duration ?? DEFAULT_DURATION;
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }

    return id;
  }

  success(message: string, options?: ToastOptions): number {
    return this.show(message, 'success', options);
  }

  error(message: string, options?: ToastOptions): number {
    return this.show(message, 'error', options);
  }

  dismiss(id: number): void {
    this.queue.update((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  clear(): void {
    this.queue.set([]);
  }
}
